/**
 * Параметры графика и расчёта (ПЛ-09), календари (ПЛ-06), захватки и свои коды работ (ПЛ-04).
 */
import {
  CALENDAR_PRESETS,
  type CalendarDef,
  hoursPerDayOf,
  kzHolidays,
  LAG_CALENDAR_TITLES,
  LAG_CALENDARS,
  type ScheduleCalendarRow,
  type ScheduleView,
} from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { toast } from '../../ui/feedback';
import { Checkbox, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';
import { fmtMoment } from './model';
import { ProgressRules } from './ProgressRules';

const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const hhmm = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const toMin = (s: string) => {
  const [h, m] = s.split(':');
  return Number(h) * 60 + Number(m ?? 0);
};

export function SettingsPanel({ view, onView }: { view: ScheduleView; onView: (v: ScheduleView) => void }) {
  const s = view.schedule;
  const ro = !view.can.edit;
  const approved = s.status === 'approved';
  const [f, setF] = useState({
    name: s.name,
    startDate: s.startDate,
    dataDay: s.dataDate?.slice(0, 10) ?? '',
    dataTime: s.dataDate?.slice(11, 16) ?? '',
    mustFinishBy: s.mustFinishBy?.slice(0, 10) ?? '',
    calendarId: s.calendarId ?? '',
    ...s.settings,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () =>
      api<ScheduleView>(`/schedules/${s.id}`, {
        method: 'PATCH',
        body: approved
          ? { name: f.name, dataDate: f.dataDay ? `${f.dataDay}T${f.dataTime || '00:00'}` : null }
          : {
              name: f.name,
              startDate: f.startDate,
              dataDate: f.dataDay ? `${f.dataDay}T${f.dataTime || '00:00'}` : null,
              mustFinishBy: f.mustFinishBy ? `${f.mustFinishBy}T23:59` : null,
              calendarId: f.calendarId || null,
              settings: {
                outOfSequence: f.outOfSequence,
                floatFrom: f.floatFrom,
                floatType: f.floatType,
                lagCalendar: f.lagCalendar,
                criticalBy: f.criticalBy,
                criticalFloatDays: Number(f.criticalFloatDays) || 0,
                nearCriticalDays: Number(f.nearCriticalDays) || 0,
                reopenByChange: Boolean(f.reopenByChange),
              },
            },
      }),
    onSuccess: (v) => {
      onView(v);
      toast('Параметры сохранены, график пересчитан');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const [cal, setCal] = useState<ScheduleCalendarRow | 'new' | null>(null);
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Параметры графика и расчёта"
        actions={
          !ro ? (
            <Button
              variant="primary"
              loading={save.isPending}
              onClick={() => save.mutate()}
              className="max-md:h-11"
            >
              Сохранить и пересчитать
            </Button>
          ) : null
        }
      >
        <fieldset disabled={ro} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <LField label="Название графика" className="sm:col-span-2">
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} className="max-md:h-11" />
          </LField>
          <LField label="Дата отчёта" hint="Оставшаяся работа планируется не раньше неё">
            <Input
              type="date"
              value={f.dataDay}
              onChange={(e) => set('dataDay', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Время отчёта">
            <Input
              type="time"
              value={f.dataTime}
              onChange={(e) => set('dataTime', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
        </fieldset>
        <fieldset
          disabled={ro || approved}
          className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <LField label="Начало проекта">
            <Input
              type="date"
              value={f.startDate}
              onChange={(e) => set('startDate', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Директивное окончание" hint="Поздние даты — от него">
            <Input
              type="date"
              value={f.mustFinishBy}
              onChange={(e) => set('mustFinishBy', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Календарь графика">
            <Select
              value={f.calendarId}
              onChange={(e) => set('calendarId', e.target.value)}
              className="max-md:h-11"
            >
              {view.calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Календарь задержек">
            <Select
              value={f.lagCalendar}
              onChange={(e) => set('lagCalendar', e.target.value as typeof f.lagCalendar)}
              className="max-md:h-11"
            >
              {LAG_CALENDARS.map((c) => (
                <option key={c} value={c}>
                  {LAG_CALENDAR_TITLES[c]}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Факт вне последовательности">
            <Select
              value={f.outOfSequence}
              onChange={(e) => set('outOfSequence', e.target.value as typeof f.outOfSequence)}
              className="max-md:h-11"
            >
              <option value="retained">Сохранение логики</option>
              <option value="override">Приоритет факта</option>
            </Select>
          </LField>
          <LField label="Резерв считается">
            <Select
              value={f.floatFrom}
              onChange={(e) => set('floatFrom', e.target.value as typeof f.floatFrom)}
              className="max-md:h-11"
            >
              <option value="project">От окончания проекта</option>
              <option value="open_ends">От каждой работы без последователей</option>
            </Select>
          </LField>
          <LField label="Полный резерв">
            <Select
              value={f.floatType}
              onChange={(e) => set('floatType', e.target.value as typeof f.floatType)}
              className="max-md:h-11"
            >
              <option value="finish">По окончанию</option>
              <option value="start">По началу</option>
              <option value="smallest">Наименьший</option>
            </Select>
          </LField>
          <LField label="Критический путь">
            <Select
              value={f.criticalBy}
              onChange={(e) => set('criticalBy', e.target.value as typeof f.criticalBy)}
              className="max-md:h-11"
            >
              <option value="float">По резерву</option>
              <option value="longest">По самой длинной цепочке</option>
            </Select>
          </LField>
          <LField label="Критично при резерве не больше, раб. дн">
            <Input
              inputMode="decimal"
              value={String(f.criticalFloatDays)}
              onChange={(e) => set('criticalFloatDays', Number(e.target.value.replace(',', '.')) || 0)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Почти критично, раб. дн">
            <Input
              inputMode="decimal"
              value={String(f.nearCriticalDays)}
              onChange={(e) => set('nearCriticalDays', Number(e.target.value.replace(',', '.')) || 0)}
              className="max-md:h-11"
            />
          </LField>
          <Checkbox
            className="min-h-11 items-center sm:col-span-2 md:min-h-0"
            label="Утверждённый график возвращается в работу только по утверждённому запросу на изменение"
            checked={Boolean(f.reopenByChange)}
            onChange={(e) => set('reopenByChange', e.target.checked)}
          />
        </fieldset>
        {approved ? (
          <p className="mt-3 text-[13px] text-ink-2">
            График утверждён: параметры расчёта меняются через запрос на изменение, дата отчёта — как обычно.
          </p>
        ) : null}
      </Panel>

      <Panel
        title="Календари"
        meta="5/2, 6/1, вахты, смены, праздники РК, сезонные ограничения"
        actions={
          !ro ? (
            <Button icon={<Plus className="size-4" />} onClick={() => setCal('new')} className="max-md:h-11">
              Добавить календарь
            </Button>
          ) : null
        }
        bodyClassName="p-0"
      >
        <ul className="divide-y divide-line">
          {view.calendars.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="flex min-h-[52px] w-full flex-col px-5 py-2.5 text-left hover:bg-surface-2"
                onClick={() => setCal(c)}
              >
                <span className="text-[14px] font-medium">
                  {c.name}
                  {c.id === s.calendarId ? (
                    <span className="ml-2 text-[12px] text-ink-2">календарь графика</span>
                  ) : null}
                </span>
                <span className="text-[12px] text-ink-2">
                  {c.def.rotation
                    ? `вахта ${c.def.rotation.on}/${c.def.rotation.off} с ${fmtMoment(c.def.rotation.anchor)}`
                    : c.def.week
                        .map((d, i) => (d.length ? WD[i] : null))
                        .filter(Boolean)
                        .join(', ')}
                  {' · '}
                  {String(c.def.hoursPerDay).replace('.', ',')} ч в день
                  {c.def.holidays === 'kz' ? ' · праздники РК' : ''}
                  {c.def.exceptions.length ? ` · исключений ${c.def.exceptions.length}` : ''}
                  {c.def.seasonal.length ? ` · сезонных ограничений ${c.def.seasonal.length}` : ''}
                  {c.projectId ? '' : ' · календарь компании'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      <ZonesAndCodes view={view} onView={onView} />
      {cal ? (
        <CalendarDialog
          view={view}
          cal={cal === 'new' ? null : cal}
          onClose={() => setCal(null)}
          onView={onView}
        />
      ) : null}
      <ProgressRules />
    </div>
  );
}

function CalendarDialog({
  view,
  cal,
  onClose,
  onView,
}: {
  view: ScheduleView;
  cal: ScheduleCalendarRow | null;
  onClose: () => void;
  onView: (v: ScheduleView) => void;
}) {
  const ro = !view.can.edit;
  const [preset, setPreset] = useState('5x2');
  const [name, setName] = useState(cal?.name ?? CALENDAR_PRESETS[0]!.name);
  const [def, setDef] = useState<CalendarDef>(() =>
    structuredClone((cal?.def ?? CALENDAR_PRESETS[0]!.def) as CalendarDef),
  );
  const [exc, setExc] = useState({ from: '', to: '', work: false, note: '' });
  const [sea, setSea] = useState({ from: '12-01', to: '02-28', note: 'Бетонирование в мороз' });
  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), def: { ...def, hoursPerDay: def.hoursPerDay || hoursPerDayOf(def) } };
      if (cal) await api(`/calendars/${cal.id}`, { method: 'PATCH', body });
      else await api('/calendars', { body: { ...body, projectId: view.project.id, preset } });
      return api<ScheduleView>(`/schedules/${view.schedule.id}`);
    },
    onSuccess: (v) => {
      onView(v);
      toast(cal ? 'Календарь сохранён, графики пересчитаны' : 'Календарь добавлен');
      onClose();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const year = Number((view.schedule.calcFinish ?? view.schedule.startDate).slice(0, 4));
  const shift = (i: number) => def.week[i]?.[0];
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title={cal ? cal.name : 'Новый календарь'}
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            {ro ? 'Закрыть' : 'Отмена'}
          </Button>
          {!ro ? (
            <Button
              variant="primary"
              loading={save.isPending}
              onClick={() => save.mutate()}
              disabled={!name.trim()}
              className="max-md:h-11"
            >
              Сохранить
            </Button>
          ) : null}
        </>
      }
    >
      <fieldset disabled={ro} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {!cal ? (
            <LField label="Начать с">
              <Select
                value={preset}
                onChange={(e) => {
                  const p = CALENDAR_PRESETS.find((x) => x.code === e.target.value)!;
                  setPreset(p.code);
                  setName(p.name);
                  setDef(structuredClone(p.def));
                }}
                className="max-md:h-11"
              >
                {CALENDAR_PRESETS.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </LField>
          ) : null}
          <LField label="Название">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Часов в рабочем дне" hint="Для перевода длительностей в дни">
            <Input
              inputMode="decimal"
              value={String(def.hoursPerDay)}
              onChange={(e) => setDef({ ...def, hoursPerDay: Number(e.target.value.replace(',', '.')) || 8 })}
              className="max-md:h-11"
            />
          </LField>
          <Checkbox
            label="Праздники и переносы РК"
            description={`В ${year} году: ${kzHolidays(year).size} нерабочих праздничных дней`}
            checked={def.holidays === 'kz'}
            onChange={(e) => setDef({ ...def, holidays: e.target.checked ? 'kz' : 'none' })}
          />
        </div>

        {def.rotation ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <LField label="Дней работы">
              <Input
                inputMode="numeric"
                value={String(def.rotation.on)}
                onChange={(e) =>
                  setDef({ ...def, rotation: { ...def.rotation!, on: Number(e.target.value) || 1 } })
                }
                className="max-md:h-11"
              />
            </LField>
            <LField label="Дней отдыха">
              <Input
                inputMode="numeric"
                value={String(def.rotation.off)}
                onChange={(e) =>
                  setDef({ ...def, rotation: { ...def.rotation!, off: Number(e.target.value) || 0 } })
                }
                className="max-md:h-11"
              />
            </LField>
            <LField label="Первый день заезда">
              <Input
                type="date"
                value={def.rotation.anchor}
                onChange={(e) => setDef({ ...def, rotation: { ...def.rotation!, anchor: e.target.value } })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Смена">
              <span className="flex gap-1">
                <Input
                  type="time"
                  value={hhmm(def.rotation.day[0]?.[0] ?? 480)}
                  onChange={(e) =>
                    setDef({
                      ...def,
                      rotation: {
                        ...def.rotation!,
                        day: [[toMin(e.target.value), def.rotation!.day.at(-1)?.[1] ?? 1200]],
                      },
                    })
                  }
                  className="max-md:h-11"
                  aria-label="Начало смены"
                />
                <Input
                  type="time"
                  value={hhmm(def.rotation.day.at(-1)?.[1] ?? 1200)}
                  onChange={(e) =>
                    setDef({
                      ...def,
                      rotation: {
                        ...def.rotation!,
                        day: [[def.rotation!.day[0]?.[0] ?? 480, toMin(e.target.value) || 1440]],
                      },
                    })
                  }
                  className="max-md:h-11"
                  aria-label="Конец смены"
                />
              </span>
            </LField>
          </div>
        ) : (
          <div>
            <h3 className="mb-2 text-[14px] font-semibold">Рабочая неделя</h3>
            <ul className="flex flex-col gap-2">
              {WD.map((w, i) => {
                const iv = def.week[i] ?? [];
                const on = iv.length > 0;
                return (
                  <li key={w} className="flex flex-wrap items-center gap-2">
                    <label className="flex min-h-11 w-16 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => {
                          const week = [...def.week];
                          week[i] = e.target.checked
                            ? (
                                def.week.find((d) => d.length) ?? [
                                  [540, 780],
                                  [840, 1080],
                                ]
                              ).map((x) => [...x] as [number, number])
                            : [];
                          setDef({ ...def, week });
                        }}
                      />
                      {w}
                    </label>
                    {on ? (
                      <span className="text-[13px] text-ink-2">
                        {iv.map(([a, b]) => `${hhmm(a)}–${hhmm(b)}`).join(', ')} ·{' '}
                        {Math.round((iv.reduce((s, [a, b]) => s + b - a, 0) / 60) * 10) / 10} ч
                      </span>
                    ) : (
                      <span className="text-[13px] text-ink-2">выходной</span>
                    )}
                    {on && shift(i) ? (
                      <Select
                        className="h-11 w-auto md:h-9"
                        aria-label={`Смены, ${w}`}
                        value={
                          iv.length >= 2 && iv[0]![1] === iv[1]![0]
                            ? iv.length === 3
                              ? '3'
                              : '2'
                            : iv[0]![0] === 0 && iv[0]![1] === 1440
                              ? '3'
                              : '1'
                        }
                        onChange={(e) => {
                          const week = [...def.week];
                          week[i] =
                            e.target.value === '1'
                              ? [
                                  [540, 780],
                                  [840, 1080],
                                ]
                              : e.target.value === '2'
                                ? [
                                    [420, 900],
                                    [900, 1380],
                                  ]
                                : [[0, 1440]];
                          setDef({ ...def, week });
                        }}
                      >
                        <option value="1">одна смена</option>
                        <option value="2">две смены</option>
                        <option value="3">три смены (24 ч)</option>
                      </Select>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div>
          <h3 className="mb-2 text-[14px] font-semibold">Исключения: переносы, простои, работа в выходной</h3>
          {def.exceptions.length ? (
            <ul className="mb-2 divide-y divide-line text-[13px]">
              {def.exceptions.map((x, i) => (
                <li
                  key={`${x.from}:${x.to}:${x.note ?? ''}`}
                  className="flex items-center justify-between gap-2 py-1.5"
                >
                  <span>
                    {fmtMoment(x.from)} — {fmtMoment(x.to)}: {x.work ? 'рабочие' : 'нерабочие'}
                    {x.note ? ` · ${x.note}` : ''}
                  </span>
                  <IconButton
                    label="Удалить исключение"
                    onClick={() => setDef({ ...def, exceptions: def.exceptions.filter((_, k) => k !== i) })}
                    className="size-11 md:size-9"
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex flex-wrap items-end gap-2">
            <LField label="С">
              <Input
                type="date"
                value={exc.from}
                onChange={(e) => setExc({ ...exc, from: e.target.value, to: exc.to || e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="По">
              <Input
                type="date"
                value={exc.to}
                onChange={(e) => setExc({ ...exc, to: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Дни">
              <Select
                value={exc.work ? '1' : '0'}
                onChange={(e) => setExc({ ...exc, work: e.target.value === '1' })}
                className="max-md:h-11"
              >
                <option value="0">нерабочие</option>
                <option value="1">рабочие</option>
              </Select>
            </LField>
            <LField label="Причина" className="min-w-[160px] flex-1">
              <Input
                value={exc.note}
                onChange={(e) => setExc({ ...exc, note: e.target.value })}
                maxLength={200}
                className="max-md:h-11"
              />
            </LField>
            <Button
              disabled={!exc.from || !exc.to || exc.to < exc.from}
              onClick={() => {
                setDef({
                  ...def,
                  exceptions: [
                    ...def.exceptions,
                    { from: exc.from, to: exc.to, work: exc.work, note: exc.note || null },
                  ],
                });
                setExc({ from: '', to: '', work: false, note: '' });
              }}
              className="max-md:h-11"
            >
              Добавить
            </Button>
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-[14px] font-semibold">Сезонные ограничения (каждый год)</h3>
          {def.seasonal.length ? (
            <ul className="mb-2 divide-y divide-line text-[13px]">
              {def.seasonal.map((x, i) => (
                <li
                  key={`${x.from}:${x.to}:${x.note ?? ''}`}
                  className="flex items-center justify-between gap-2 py-1.5"
                >
                  <span>
                    {x.from.split('-').reverse().join('.')} — {x.to.split('-').reverse().join('.')}
                    {x.note ? ` · ${x.note}` : ''}
                  </span>
                  <IconButton
                    label="Удалить ограничение"
                    onClick={() => setDef({ ...def, seasonal: def.seasonal.filter((_, k) => k !== i) })}
                    className="size-11 md:size-9"
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-2 text-[13px] text-ink-2">
              Например, календарь бетонных работ: не работать с 1 декабря по 28 февраля.
            </p>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <LField label="С (ДД.ММ)">
              <Input
                value={sea.from.split('-').reverse().join('.')}
                onChange={(e) => setSea({ ...sea, from: e.target.value.split('.').reverse().join('-') })}
                placeholder="01.12"
                className="w-24 max-md:h-11"
              />
            </LField>
            <LField label="По (ДД.ММ)">
              <Input
                value={sea.to.split('-').reverse().join('.')}
                onChange={(e) => setSea({ ...sea, to: e.target.value.split('.').reverse().join('-') })}
                placeholder="28.02"
                className="w-24 max-md:h-11"
              />
            </LField>
            <LField label="Что ограничено" className="min-w-[160px] flex-1">
              <Input
                value={sea.note}
                onChange={(e) => setSea({ ...sea, note: e.target.value })}
                maxLength={200}
                className="max-md:h-11"
              />
            </LField>
            <Button
              disabled={!/^\d{2}-\d{2}$/.test(sea.from) || !/^\d{2}-\d{2}$/.test(sea.to)}
              onClick={() =>
                setDef({
                  ...def,
                  seasonal: [...def.seasonal, { from: sea.from, to: sea.to, note: sea.note || null }],
                })
              }
              className="max-md:h-11"
            >
              Добавить
            </Button>
          </div>
        </div>
      </fieldset>
    </Dialog>
  );
}

function ZonesAndCodes({ view, onView }: { view: ScheduleView; onView: (v: ScheduleView) => void }) {
  const ro = !view.can.edit;
  const [zone, setZone] = useState({ code: '', name: '' });
  const [code, setCode] = useState({ name: '', values: '' });
  const m = useMutation({
    mutationFn: (x: { url: string; body?: unknown; method?: 'POST' | 'DELETE' }) =>
      api<ScheduleView | { ok: true }>(x.url, { method: x.method ?? 'POST', body: x.body }),
    onSuccess: async (r) => {
      if ('schedule' in r) onView(r);
      else onView(await api<ScheduleView>(`/schedules/${view.schedule.id}`));
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Panel title="Захватки" meta="участки фронта работ">
        {view.zones.length ? (
          <ul className="mb-3 divide-y divide-line text-[14px]">
            {view.zones.map((z) => (
              <li key={z.id} className="flex items-center justify-between py-1.5">
                <span>
                  {z.code ? `${z.code} ` : ''}
                  {z.name}
                </span>
                {!ro ? (
                  <IconButton
                    label="Удалить захватку"
                    onClick={() =>
                      m.mutate({ url: `/schedules/${view.schedule.id}/zones/${z.id}`, method: 'DELETE' })
                    }
                    className="size-11 md:size-9"
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-3 text-[13px] text-ink-2">Захваток пока нет.</p>
        )}
        {!ro ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!zone.name.trim()) return;
              m.mutate({
                url: `/schedules/${view.schedule.id}/zones`,
                body: { code: zone.code.trim() || null, name: zone.name.trim() },
              });
              setZone({ code: '', name: '' });
            }}
          >
            <LField label="Код">
              <Input
                value={zone.code}
                onChange={(e) => setZone({ ...zone, code: e.target.value })}
                className="w-24 max-md:h-11"
              />
            </LField>
            <LField label="Название" className="min-w-[160px] flex-1">
              <Input
                value={zone.name}
                onChange={(e) => setZone({ ...zone, name: e.target.value })}
                placeholder="ПК0–ПК10"
                className="max-md:h-11"
              />
            </LField>
            <Button type="submit" className="max-md:h-11">
              Добавить
            </Button>
          </form>
        ) : null}
      </Panel>
      <Panel title="Свои коды работ" meta="для группировки и фильтров">
        {view.codeTypes.length ? (
          <ul className="mb-3 divide-y divide-line text-[14px]">
            {view.codeTypes.map((t) => (
              <li key={t.id} className="py-1.5">
                <b>{t.name}</b>:{' '}
                <span className="text-ink-2">
                  {t.values.map((v) => v.value).join(', ') || 'нет значений'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-3 text-[13px] text-ink-2">Например, «Этап договора» или «Участок».</p>
        )}
        {!ro ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!code.name.trim()) return;
              const values = code.values
                .split(/[,;\n]/)
                .map((x) => x.trim())
                .filter(Boolean)
                .map((value) => ({ value }));
              m.mutate({
                url: `/schedules/${view.schedule.id}/code-types`,
                body: { name: code.name.trim(), values },
              });
              setCode({ name: '', values: '' });
            }}
          >
            <LField label="Название кода">
              <Input
                value={code.name}
                onChange={(e) => setCode({ ...code, name: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Значения через запятую" className="min-w-[160px] flex-1">
              <Input
                value={code.values}
                onChange={(e) => setCode({ ...code, values: e.target.value })}
                placeholder="Этап 1, Этап 2"
                className="max-md:h-11"
              />
            </LField>
            <Button type="submit" className="max-md:h-11">
              Добавить
            </Button>
          </form>
        ) : null}
      </Panel>
    </div>
  );
}
